<template>
  <div ref="containerRef" class="ship-topo-graph">
    <v-chart ref="chartRef" :option="option" @click="onChartClick" />
  </div>
</template>
<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

// echarts
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { GraphChart } from "echarts/charts";
import { ToolboxComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import router from "@/router";
import { Endpoint } from "@/types/endpoint";
import { some } from "lodash-es";

use([ToolboxComponent, GraphChart, CanvasRenderer]);

const { t } = useI18n();

const props = defineProps<{
  shipId: string;
}>();

interface TopoGraphData {
  id: string;
  name: string;
  x: number;
  y: number;
  connection: number;
}

interface TopoGraphEdge {
  source: string;
  target: string;
  value: string;
}

// 获取数据
const data = ref<TopoGraphData[]>([]);
const edge = ref<TopoGraphEdge[]>([]);
const endpointList = ref<Endpoint[]>([]);
const getData = async () => {
  // TODO: getEndpointListApi / getCustomDeviceListApi 后端接口尚未实现，先跳过
  // 后续接口就绪后放开以下代码即可恢复拓扑图渲染
  return;
  /* eslint-disable */
  // const [endpoints, devices] = await Promise.all([
  //   getEndpointListApi(props.shipId),
  //   getCustomDeviceListApi(props.shipId),
  // ]);
  // if (!endpoints || endpoints.length == 0) return;
  // endpointList.value = endpoints;
  // data.value.push({
  //   id: "center",
  //   name: "中心节点",
  //   x: 0,
  //   y: 0,
  //   connection: 3,
  // });
  // endpoints.forEach((endpoint, index) => {
  //   if (!endpoint.name || !endpoint.status) return;
  //   data.value.push({
  //     id: endpoint.id,
  //     name: endpoint.name,
  //     x: index * 200,
  //     y: -100,
  //     connection: endpoint.status.id,
  //   });
  //   if (index > 0) {
  //     edge.value.push({
  //       source: endpoints[index - 1].id,
  //       target: endpoint.id || "",
  //       value: "",
  //     });
  //   }
  // });
  // if (!devices || devices.length == 0) return;
  // const deviceList = Object.values(groupBy(devices, "type"));
  // const length = deviceList.length;
  // deviceList.forEach((devices, index) => {
  //   const x = -100 * length + index * 200 + 100;
  //   edge.value.push({
  //     source: endpoints[0].id,
  //     target: devices[0].id || "",
  //     value: devices[0].type,
  //   });
  //   let tempId = devices[0].id || "";
  //   devices.forEach((device, index) => {
  //     if (!device.id || !device.connection) return;
  //     data.value.push({
  //       id: device.id,
  //       name: device.name,
  //       x: x,
  //       y: index * 200 + 200,
  //       connection: device.connection.id,
  //     });
  //     if (index > 0) {
  //       edge.value.push({
  //         source: tempId,
  //         target: device.id,
  //         value: "",
  //       });
  //     }
  //     tempId = device.id;
  //   });
  // });
};
// echarts的配置信息
const option = ref({
  toolbox: {
    itemSize: 20,
    itemGap: 12,
    feature: {
      restore: {
        title: t("STG.restore"),
      },
      saveAsImage: {
        type: "png",
        title: t("STG.saveAsImage"),
        name: t("STG.imageName", { id: props.shipId }),
      },
    },
  },
  series: [
    {
      coordinateSystem: null,
      type: "graph",
      symbol: "rect",
      animation: false,
      symbolSize: (value: any, params: any) => {
        const nameLength = params.data.name.length;
        let width = nameLength * 12 + 20; // 文字长度动态调整宽度
        if (width > 170) width = 170;
        const height = 30; // 固定高度
        return [width, height];
      },
      roam: true,
      scaleLimit: {
        min: 1,
        max: 2,
      },
      nodeScaleRatio: 1,
      label: {
        show: true,
        fontSize: 14,
        overflow: "truncate",
        width: 150,
      },
      data: data.value,
      links: edge.value,
      itemStyle: {
        color: function (params: any) {
          return params.data.connection == 1 ? "#a5ffa3" : "#959595";
        },
      },
      edgeSymbol: ["none", "none"],
      edgeLabel: {
        fontSize: 14,
        show: true,
        formatter: "{c}",
      },
    },
  ],
});
// 点击事件处理
const onChartClick = (params: any) => {
  console.log(params);
  if (params.dataType != "node") return;
  if (params.data && params.data.name) {
    if (!params.data.id || params.data.id == "") return;
    if (!params.data.connection || params.data.connection == 0) {
      ElMessage.error(t("STG.disconnectError"));
    } else {
      if (some(endpointList.value, { id: params.data.id })) {
        router.push({
          name: "endpointInfo",
          params: { endpointId: params.data.id },
        });
      } else {
        router.push({
          name: "customDeviceInfo",
          params: { customDeviceId: params.data.id },
        });
      }
    }
  } else {
    ElMessage.error(t("STG.dataError"));
  }
};

onMounted(async () => {
  await getData();
  await nextTick();
  setupResizeObserver();
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (resizeRafId !== null) {
    cancelAnimationFrame(resizeRafId);
    resizeRafId = null;
  }
});

const containerRef = ref<HTMLDivElement>();
const chartRef = ref<InstanceType<typeof VChart> | null>(null);
let resizeObserver: ResizeObserver | null = null;
let resizeRafId: number | null = null;

const setupResizeObserver = () => {
  if (!containerRef.value || typeof ResizeObserver === "undefined") return;
  resizeObserver = new ResizeObserver(() => {
    if (resizeRafId !== null) cancelAnimationFrame(resizeRafId);
    resizeRafId = requestAnimationFrame(() => {
      const instance = chartRef.value?.chart as
        | { resize?: () => void }
        | undefined;
      if (instance && typeof instance.resize === "function") {
        instance.resize();
      }
      resizeRafId = null;
    });
  });
  resizeObserver.observe(containerRef.value);
};
</script>
<style lang="less" scoped>
.ship-topo-graph {
  width: 100%;
  height: 100%;
}
</style>
